// Global Instructions Rule Applied!
//
// Deal form — Wire tab (signed-off Mockup 6). Wire instructions per deal with
// per-beneficiary auto-fill (latest details for the same company suggested on
// empty deals, plus company-derived beneficiary/address defaults), completion
// facts (value date, confirmation reference), and the wire-request email text
// with one-click copy. Fields save on entry (owner 2026-08-14): each field
// commit sends the full snapshot (deal_wire__save is a full-row upsert). Access
// is gated by the cfg email list
// (deal_execution/wire_authorised_emails) until auth groups exist —
// unauthorised users see a lock note, and the backend enforces on save.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Input, Spin, Tag } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { CopyOutlined, LockOutlined } from '@ant-design/icons'
import DealTransactionsTable from './DealTransactionsTable.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { ContactInput } from '../../../core/components/InputManager/components/ContactInput.tsx'
import { FieldValue } from '../../../core/components/InputManager/types.ts'
import dayjs from 'dayjs'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  fetchExecutionBoard,
  fetchExecutionContext,
  fetchWireInstructions,
  saveWireInstructions
} from '../services/dealExecutionService.ts'
import {
  DealExecutionBoardRecord,
  DealExecutionContext
} from '../../../state_manager/data_models/deal_execution_board_model.ts'

interface WireTabProps {
  dealId: string | null
  /** The deal's company — scopes the confirmation-contact picker. The board
   *  row only covers deals on an execution board, so the form passes it. */
  companyId?: string | null
  /** Hands the loader to the deal form so its Refresh button reloads this
   *  tab (same contract as DocumentsTab). */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

const WIRE_TEXT_FIELDS = [
  ['beneficiary', 'Beneficiary'],
  ['beneficiary_address', 'Beneficiary address'],
  ['bank', 'Bank'],
  ['bank_address', 'Bank address'],
  ['swift_bic', 'SWIFT / BIC'],
  ['account_iban', 'Account Nr. / IBAN'],
  ['aba_routing', 'ABA Routing Nr.'],
  ['payment_reference', 'Payment reference']
] as const

// Contact for confirmation on the wire-request email (founder/employee)
const CONTACT_FIELDS = [
  ['contact_name', 'Contact name'],
  ['contact_title', 'Contact title'],
  ['contact_phone', 'Contact tel nr.']
] as const

export default function WireTab({
  dealId,
  companyId = null,
  onRefreshReady = null
}: WireTabProps): React.ReactElement {
  const token = () => getAccessToken(store.getState().auth)
  const [loading, setLoading] = useState(true)
  const [authorised, setAuthorised] = useState(false)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [autoFilled, setAutoFilled] = useState(false)
  const [boardRow, setBoardRow] = useState<DealExecutionBoardRecord | null>(null)
  const [context, setContext] = useState<DealExecutionContext | null>(null)
  // Chosen contact in the confirmation-contact picker (fills the text
  // fields; nothing is stored beyond them — snapshot semantics)
  const [contactPick, setContactPick] = useState<FieldValue>(null)
  // Last-persisted snapshot — commits are skipped while nothing changed
  const savedRef = useRef<Record<string, string> | null>(null)
  const fieldsRef = useRef<Record<string, string>>({})
  fieldsRef.current = fields

  // Reusable loader: mount, the deal form's Refresh button, and the
  // refresh-on-activate pass all drive it. Safe to re-run — fields save on
  // entry, so anything the operator typed was committed on blur before the
  // tab could go inactive; the linked-transactions list below was otherwise
  // stale until a hard refresh (owner report 2026-08-26).
  const load = useCallback(async (): Promise<void> => {
    if (!dealId) return
    setLoading(true)
    try {
      const [wire, board, ctx] = await Promise.all([
        fetchWireInstructions(token(), dealId),
        fetchExecutionBoard(token(), { dealId }),
        fetchExecutionContext(token(), dealId)
      ])
      setAuthorised(wire.data.authorised)
      setBoardRow(board.data[0] ?? null)
      setContext(ctx.data)
      // Prefill precedence per field: own row > suggested (latest wire
      // details for the same company) > company-derived defaults
      // (beneficiary legal name / address).
      const source = wire.data.own || wire.data.suggested
      const next: Record<string, string> = {}
      if (source) {
        for (const [key] of WIRE_TEXT_FIELDS) next[key] = String(source[key] ?? '')
        for (const [key] of CONTACT_FIELDS) next[key] = String(source[key] ?? '')
        next.notes = String(source.notes ?? '')
        next.value_date = String(source.value_date ?? '')
        next.confirmation_reference = String(source.confirmation_reference ?? '')
      }
      // The persisted truth is the own row only — prefills from the
      // suggestion or the defaults stay unsaved until a field commits.
      savedRef.current = wire.data.own ? { ...next } : null
      const defaults = wire.data.defaults || {}
      let defaultsApplied = false
      for (const key of ['beneficiary', 'beneficiary_address'] as const) {
        if (!next[key] && defaults[key]) {
          next[key] = String(defaults[key])
          defaultsApplied = true
        }
      }
      if (source || defaultsApplied) setFields(next)
      setAutoFilled(!wire.data.own && (!!wire.data.suggested || defaultsApplied))
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])
  const activateRef = useRefreshOnActivate(load)

  // The asset transactions are the source of the wire amount and the paying
  // fund — one Payor/Amount pair per transaction; commitments are the
  // fallback until a transaction is linked.
  const emailText = useMemo(() => {
    const code = context?.currencies__code || ''
    const fmtAmount = (v: number): string =>
      `${code ? `${code} ` : ''}${Number(v).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`
    const line = (label: string, value: string): string => `${`${label}:`.padEnd(19, ' ')}${value}`
    const transactions = (context?.transactions || []).filter(
      (t) => t.asset_transactions__amount != null
    )
    const payorPairs = transactions.length
      ? transactions.map(
          (t) =>
            `${line('Payor', t.funds__name || '—')}\n${line(
              'Amount',
              fmtAmount(t.asset_transactions__amount as number)
            )}`
        )
      : (boardRow?.prog__commitments || []).map(
          (c) =>
            `${line('Payor', c.fund_name || '—')}\n${line(
              'Amount',
              c.amount != null ? fmtAmount(Number(c.amount)) : '—'
            )}`
        )
    const contact = [fields.contact_name, fields.contact_title, fields.contact_phone]
      .filter(Boolean)
      .join(', ')
    return `Please arrange the following wire:

${payorPairs.join('\n\n') || `${line('Payor', '—')}\n${line('Amount', '—')}`}

${line('Beneficiary', fields.beneficiary || '—')}
${line('Address', fields.beneficiary_address || '—')}
${line('Bank', fields.bank || '—')}
${line('Bank address', fields.bank_address || '—')}

${line('IBAN / Account', fields.account_iban || '')}
${line('SWIFT/BIC', fields.swift_bic || '')}${
      fields.aba_routing ? `\n${line('ABA Routing', fields.aba_routing)}` : ''
    }
${line('Reference', fields.payment_reference || '')}

Contact for confirmation:
${contact || 'Founder/Employee name, title, tel nr.'}`
  }, [fields, context, boardRow])

  // Persist the full snapshot (the save RPC is a full-row upsert) — called
  // on every field commit; skipped while nothing changed since the last save.
  const commit = async (snapshot: Record<string, string>): Promise<void> => {
    if (!dealId) return
    if (savedRef.current && JSON.stringify(savedRef.current) === JSON.stringify(snapshot)) return
    // No row yet and nothing entered — tabbing through an empty form must
    // not create one
    if (!savedRef.current && !Object.values(snapshot).some((v) => v !== '')) return
    const result = await saveWireInstructions(token(), dealId, snapshot)
    if (result.success) {
      savedRef.current = { ...snapshot }
      setAutoFilled(false)
    } else {
      showErrorToast(result.error || 'Failed to save wire instructions')
    }
  }

  // Blur commit for controlled Inputs — fieldsRef avoids stale closures
  const commitCurrent = (): void => {
    void commit(fieldsRef.current)
  }

  // Set + commit in one step (DatePicker / contact picker)
  const commitField = (updates: Record<string, string>): void => {
    const next = { ...fieldsRef.current, ...updates }
    setFields(next)
    void commit(next)
  }

  // Contact picker → snapshot the contact's details into the text fields
  const handleContactPick = (picked: FieldValue): void => {
    setContactPick(picked)
    if (!picked || typeof picked !== 'object') return
    const p = picked as { label?: string; mobile_phone?: string; job_title?: string }
    commitField({
      contact_name: p.label || '',
      contact_title: p.job_title || '',
      contact_phone: p.mobile_phone || ''
    })
  }

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(emailText)
      toast.success('Wire request email copied')
    } catch {
      showErrorToast('Copy failed — select and copy the text manually')
    }
  }

  if (loading) {
    return (
      <div className='flex justify-center py-16'>
        <Spin />
      </div>
    )
  }

  if (!authorised) {
    return (
      <div className='max-w-xl mx-auto text-center py-16 text-gray-500'>
        <LockOutlined className='text-2xl mb-3' />
        <p className='text-sm'>
          Wire instructions are restricted. Access is granted via Configuration
          (deal_execution / wire_authorised_emails) until user groups arrive.
        </p>
      </div>
    )
  }

  const setField = (key: string, value: string): void =>
    setFields((prev) => ({ ...prev, [key]: value }))

  return (
    <div ref={activateRef} className='px-2 py-4 space-y-4'>
      {autoFilled && (
        <Alert
          type='info'
          showIcon
          message='Auto-filled from the latest wire details for this company — fields save as you edit them; confirm to keep these values for this deal as they are.'
          action={
            <Button size='small' type='primary' onClick={commitCurrent}>
              Confirm for this deal
            </Button>
          }
        />
      )}

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <FormSection title='Wire Instructions' titleColor='blue' scrollable={false}>
          {WIRE_TEXT_FIELDS.map(([key, label]) => (
            <div key={key} className='flex items-center gap-3 py-1'>
              <span className='w-40 flex-none text-xs text-gray-500'>{label}</span>
              <Input
                size='small'
                value={fields[key] || ''}
                onChange={(e) => setField(key, e.target.value)}
                onBlur={commitCurrent}
              />
            </div>
          ))}
          <div className='text-xs font-semibold text-gray-700 mt-3 mb-1'>Contact for confirmation</div>
          <div className='flex items-center gap-3 py-1'>
            <span className='w-40 flex-none text-xs text-gray-500'>Select contact</span>
            <ContactInput
              size='small'
              mode='single'
              value={contactPick}
              companyId={companyId || boardRow?.accounts__id || null}
              allowAddNew={false}
              placeholder='Search company contacts…'
              onChange={handleContactPick}
            />
          </div>
          {CONTACT_FIELDS.map(([key, label]) => (
            <div key={key} className='flex items-center gap-3 py-1'>
              <span className='w-40 flex-none text-xs text-gray-500'>{label}</span>
              <Input
                size='small'
                value={fields[key] || ''}
                onChange={(e) => setField(key, e.target.value)}
                onBlur={commitCurrent}
              />
            </div>
          ))}
          <div className='flex items-center gap-3 py-1'>
            <span className='w-40 flex-none text-xs text-gray-500'>Notes</span>
            <Input.TextArea
              rows={2}
              value={fields.notes || ''}
              onChange={(e) => setField('notes', e.target.value)}
              onBlur={commitCurrent}
            />
          </div>
          <div className='flex items-center gap-3 py-1'>
            <span className='w-40 flex-none text-xs text-gray-500'>Value date</span>
            <DatePicker
              size='small'
              className='w-full'
              format='MM/DD/YYYY'
              value={fields.value_date ? dayjs(fields.value_date) : null}
              onChange={(value) => commitField({ value_date: value ? value.format('YYYY-MM-DD') : '' })}
            />
          </div>
          <div className='flex items-center gap-3 py-1'>
            <span className='w-40 flex-none text-xs text-gray-500'>Confirmation ref</span>
            <Input
              size='small'
              value={fields.confirmation_reference || ''}
              onChange={(e) => setField('confirmation_reference', e.target.value)}
              onBlur={commitCurrent}
            />
          </div>
        </FormSection>

        <FormSection
          title='Wire Request Email'
          titleColor='gray'
          scrollable={false}
          headerActions={
            <>
              <Tag color='gold' className='m-0'>
                authorised users only
              </Tag>
              <Button size='small' icon={<CopyOutlined />} onClick={handleCopy}>
                Copy email text
              </Button>
            </>
          }
        >
          <pre className='text-xs leading-relaxed bg-gray-50 border border-gray-100 rounded p-3 whitespace-pre-wrap font-mono'>
            {emailText}
          </pre>
        </FormSection>
      </div>

      <FormSection title='Asset Transactions' titleColor='green' scrollable={false}>
        <div className='text-xs text-gray-500 mb-2'>Source of the wire amount and the paying fund</div>
        <DealTransactionsTable
          transactions={context?.transactions || []}
          currencySymbol={boardRow?.currencies__symbol}
          emptyText='No asset transactions linked yet — the email falls back to the committed amounts until one is booked against this deal.'
        />
      </FormSection>
    </div>
  )
}
